document.documentElement.classList.remove('no-js');

const initMetabolixHeroSlider = (slider) => {
  if (!slider || slider.dataset.sliderReady === 'true') return;

  const track = slider.querySelector('[data-slider-track]');
  const slides = Array.from(slider.querySelectorAll('[data-hero-slide]'));
  const dots = Array.from(slider.querySelectorAll('[data-slider-dot]'));
  const previousButton = slider.querySelector('[data-slider-previous]');
  const nextButton = slider.querySelector('[data-slider-next]');
  const pauseButton = slider.querySelector('[data-slider-pause]');
  const pauseIcon = slider.querySelector('[data-slider-pause-icon]');
  const pauseLabel = slider.querySelector('[data-slider-pause-label]');
  const status = slider.querySelector('[data-slider-status]');

  if (!track || slides.length === 0) return;

  slider.dataset.sliderReady = 'true';

  let currentIndex = Math.max(0, slides.findIndex((slide) => slide.classList.contains('is-active')));
  let timer = null;
  let manuallyPaused = false;
  let interactionPaused = false;
  let sectionIsVisible = true;
  let pointerStartX = null;

  const autoplayEnabled = slider.dataset.autoplay === 'true' && slides.length > 1;
  const pauseOnHover = slider.dataset.pauseOnHover === 'true';
  const interval = Math.max(3000, Number.parseInt(slider.dataset.interval, 10) || 6000);
  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  const clearTimer = () => {
    if (timer !== null) {
      window.clearTimeout(timer);
      timer = null;
    }
  };

  const canAutoplay = () => (
    autoplayEnabled
    && !reducedMotion
    && !manuallyPaused
    && !interactionPaused
    && sectionIsVisible
    && !document.hidden
  );

  const updatePauseControl = () => {
    if (!pauseButton) return;
    const isPaused = manuallyPaused || reducedMotion;
    pauseButton.setAttribute('aria-label', isPaused ? 'Play rotating slides' : 'Pause rotating slides');
    pauseButton.setAttribute('aria-pressed', String(isPaused));
    if (pauseIcon) pauseIcon.textContent = isPaused ? '▶' : 'Ⅱ';
    if (pauseLabel) pauseLabel.textContent = isPaused ? 'Play' : 'Pause';
  };

  const scheduleNext = () => {
    clearTimer();
    if (!canAutoplay()) return;
    timer = window.setTimeout(() => {
      timer = null;
      showSlide(currentIndex + 1, false);
    }, interval);
  };

  const showSlide = (requestedIndex, announce = true) => {
    currentIndex = (requestedIndex + slides.length) % slides.length;
    track.style.transform = `translate3d(-${currentIndex * 100}%, 0, 0)`;

    slides.forEach((slide, index) => {
      const isActive = index === currentIndex;
      slide.classList.toggle('is-active', isActive);
      slide.setAttribute('aria-hidden', String(!isActive));
      slide.toggleAttribute('inert', !isActive);
      slide.querySelectorAll('a, button, input, select, textarea').forEach((element) => {
        if (isActive) element.removeAttribute('tabindex');
        else element.setAttribute('tabindex', '-1');
      });
    });

    dots.forEach((dot, index) => {
      dot.setAttribute('aria-current', String(index === currentIndex));
    });

    if (announce && status) {
      const label = slides[currentIndex].dataset.slideLabel || '';
      status.textContent = `Slide ${currentIndex + 1} of ${slides.length}${label ? `: ${label}` : ''}`;
    }

    scheduleNext();
  };

  const setInteractionPaused = (isPaused) => {
    interactionPaused = isPaused;
    scheduleNext();
  };

  previousButton?.addEventListener('click', () => showSlide(currentIndex - 1));
  nextButton?.addEventListener('click', () => showSlide(currentIndex + 1));
  dots.forEach((dot) => {
    dot.addEventListener('click', () => showSlide(Number.parseInt(dot.dataset.sliderDot, 10) || 0));
  });

  pauseButton?.addEventListener('click', () => {
    manuallyPaused = !manuallyPaused;
    updatePauseControl();
    scheduleNext();
  });

  slider.addEventListener('keydown', (event) => {
    if (event.target.matches('input, select, textarea')) return;
    if (event.key === 'ArrowLeft') {
      event.preventDefault();
      showSlide(currentIndex - 1);
    }
    if (event.key === 'ArrowRight') {
      event.preventDefault();
      showSlide(currentIndex + 1);
    }
  });

  if (pauseOnHover) {
    slider.addEventListener('mouseenter', () => setInteractionPaused(true));
    slider.addEventListener('mouseleave', () => setInteractionPaused(false));
    slider.addEventListener('focusin', () => setInteractionPaused(true));
    slider.addEventListener('focusout', () => {
      window.setTimeout(() => {
        if (!slider.contains(document.activeElement)) setInteractionPaused(false);
      }, 0);
    });
  }

  slider.addEventListener('pointerdown', (event) => {
    if (event.pointerType === 'mouse') return;
    pointerStartX = event.clientX;
    slider.classList.add('is-dragging');
  });

  slider.addEventListener('pointerup', (event) => {
    if (pointerStartX === null) return;
    const distance = event.clientX - pointerStartX;
    pointerStartX = null;
    slider.classList.remove('is-dragging');
    if (Math.abs(distance) < 45) return;
    showSlide(distance > 0 ? currentIndex - 1 : currentIndex + 1);
  });

  slider.addEventListener('pointercancel', () => {
    pointerStartX = null;
    slider.classList.remove('is-dragging');
  });

  const handleVisibilityChange = () => scheduleNext();
  document.addEventListener('visibilitychange', handleVisibilityChange);

  let visibilityObserver = null;
  if ('IntersectionObserver' in window) {
    visibilityObserver = new IntersectionObserver((entries) => {
      sectionIsVisible = entries[0]?.isIntersecting ?? true;
      scheduleNext();
    }, { threshold: 0.2 });
    visibilityObserver.observe(slider);
  }

  slider.metabolixSlider = {
    showSlide,
    pause() {
      manuallyPaused = true;
      updatePauseControl();
      scheduleNext();
    },
    destroy() {
      clearTimer();
      visibilityObserver?.disconnect();
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      delete slider.metabolixSlider;
      delete slider.dataset.sliderReady;
    }
  };

  updatePauseControl();
  showSlide(currentIndex, false);
};

document.addEventListener('DOMContentLoaded', () => {
  document.querySelectorAll('[data-hero-slider]').forEach(initMetabolixHeroSlider);

  const toggle = document.querySelector('[data-mobile-toggle]');
  const nav = document.querySelector('[data-mobile-nav]');
  if (toggle && nav) {
    toggle.addEventListener('click', () => {
      const isOpen = nav.classList.toggle('is-open');
      toggle.setAttribute('aria-expanded', String(isOpen));
      toggle.setAttribute('aria-label', isOpen ? 'Close menu' : 'Open menu');
    });
    nav.querySelectorAll('a').forEach((link) => link.addEventListener('click', () => {
      nav.classList.remove('is-open');
      toggle.setAttribute('aria-expanded', 'false');
      toggle.setAttribute('aria-label', 'Open menu');
    }));
  }

  document.querySelectorAll('[data-gallery-section]').forEach((section) => {
    const filters = Array.from(section.querySelectorAll('[data-gallery-filter]'));
    const items = Array.from(section.querySelectorAll('[data-gallery-item]'));
    const noResults = section.querySelector('[data-gallery-no-results]');
    const lightbox = section.querySelector('[data-gallery-lightbox]');
    const lightboxImage = section.querySelector('[data-gallery-lightbox-image]');
    const lightboxCategory = section.querySelector('[data-gallery-lightbox-category]');
    const lightboxTitle = section.querySelector('[data-gallery-lightbox-title]');
    const lightboxMeta = section.querySelector('[data-gallery-lightbox-meta]');
    const lightboxDescription = section.querySelector('[data-gallery-lightbox-description]');
    let lastGalleryTrigger = null;

    filters.forEach((filterButton) => {
      filterButton.addEventListener('click', () => {
        const selectedCategory = filterButton.dataset.galleryFilter;
        let visibleItems = 0;

        filters.forEach((button) => {
          const isSelected = button === filterButton;
          button.classList.toggle('is-active', isSelected);
          button.setAttribute('aria-pressed', String(isSelected));
        });

        items.forEach((item) => {
          const isVisible = selectedCategory === 'all' || item.dataset.category === selectedCategory;
          item.hidden = !isVisible;
          if (isVisible) visibleItems += 1;
        });

        if (noResults) noResults.hidden = visibleItems !== 0;
      });
    });

    section.querySelectorAll('[data-gallery-open]').forEach((trigger) => {
      trigger.addEventListener('click', () => {
        if (!lightbox || !lightboxImage) return;
        lastGalleryTrigger = trigger;
        lightboxImage.src = trigger.dataset.galleryImage || '';
        lightboxImage.alt = trigger.dataset.galleryAlt || '';
        lightboxCategory.textContent = trigger.dataset.galleryCategory || '';
        lightboxTitle.textContent = trigger.dataset.galleryTitle || '';
        lightboxMeta.textContent = trigger.dataset.galleryMeta || '';
        lightboxMeta.hidden = !trigger.dataset.galleryMeta;
        lightboxDescription.textContent = trigger.dataset.galleryDescription || '';
        lightboxDescription.hidden = !trigger.dataset.galleryDescription;

        if (typeof lightbox.showModal === 'function') {
          lightbox.showModal();
        } else {
          lightbox.setAttribute('open', '');
        }
        document.body.classList.add('gallery-lightbox-open');
      });
    });

    const closeLightbox = () => {
      if (!lightbox) return;
      if (typeof lightbox.close === 'function') {
        lightbox.close();
      } else {
        lightbox.removeAttribute('open');
      }
      document.body.classList.remove('gallery-lightbox-open');
      if (lastGalleryTrigger) lastGalleryTrigger.focus();
    };

    section.querySelectorAll('[data-gallery-close]').forEach((button) => {
      button.addEventListener('click', closeLightbox);
    });

    if (lightbox) {
      lightbox.addEventListener('click', (event) => {
        if (event.target === lightbox) closeLightbox();
      });
      lightbox.addEventListener('close', () => {
        document.body.classList.remove('gallery-lightbox-open');
      });
    }
  });
});

document.addEventListener('shopify:section:load', (event) => {
  if (event.target.matches?.('[data-hero-slider]')) initMetabolixHeroSlider(event.target);
  event.target.querySelectorAll?.('[data-hero-slider]').forEach(initMetabolixHeroSlider);
});

document.addEventListener('shopify:section:unload', (event) => {
  const sliders = [];
  if (event.target.matches?.('[data-hero-slider]')) sliders.push(event.target);
  event.target.querySelectorAll?.('[data-hero-slider]').forEach((slider) => sliders.push(slider));
  sliders.forEach((slider) => slider.metabolixSlider?.destroy());
});

document.addEventListener('shopify:block:select', (event) => {
  const slide = event.target.closest?.('[data-hero-slide]');
  const slider = event.target.closest?.('[data-hero-slider]');
  if (!slide || !slider?.metabolixSlider) return;
  slider.metabolixSlider.showSlide(Number.parseInt(slide.dataset.slideIndex, 10) || 0, false);
  slider.metabolixSlider.pause();
});

// WALU product gallery - switch full-resolution product pictures without cropping.
document.addEventListener('click', function (event) {
  const button = event.target.closest('[data-walu-thumb]');
  if (!button) return;
  const gallery = button.closest('[data-walu-gallery]');
  if (!gallery) return;
  const main = gallery.querySelector('[data-walu-main] img');
  if (!main) return;
  main.src = button.dataset.imageSrc;
  main.removeAttribute('srcset');
  main.removeAttribute('sizes');
  main.alt = button.dataset.imageAlt || '';
  gallery.querySelectorAll('[data-walu-thumb]').forEach(function (thumb) {
    const selected = thumb === button;
    thumb.classList.toggle('is-selected', selected);
    thumb.setAttribute('aria-pressed', String(selected));
  });
});

// Browse all three Meta-Lean bottle views from the catalogue without opening the product page.
document.addEventListener('click', function (event) {
  const button = event.target.closest('[data-mbx-card-view]');
  if (!button) return;
  const card = button.closest('[data-mbx-card]');
  const image = card?.querySelector('[data-mbx-card-image]');
  if (!image || !button.dataset.image) return;
  image.src = button.dataset.image;
  image.alt = button.dataset.alt || image.alt;
  card.querySelectorAll('[data-mbx-card-view]').forEach(function (b) {
    const active = b === button;
    b.classList.toggle('is-active', active);
    b.setAttribute('aria-pressed', String(active));
  });
});
